import {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
  lazy,
  Suspense,
} from "react";
import { Route, Routes } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import { firebase_auth } from "@src/utility_functions/database_operations/config/firebaseConfig";
import { Header } from "@src/components/Header/Header";
import { LockupContainer } from "@src/components/Static/LockupContainer";
import { Modal } from "@src/components/Modal/Modal";
import { ModalContext } from "@src/utility_functions/context/ModalContext";
import { Footer } from "@src/components/Footer/Footer";
import { useUserDocumentQuery } from "@src/utility_functions/database_operations/users/readUser/useUserDocumentQuery";
import { useModalHistory } from "@src/utility_functions/customHooks/useModalHistory";
import { ErrorBoundary } from "@src/components/Utility/ErrorBoundary";
import type { ChartHostElement } from "@src/types/timeline";

const HomePage = lazy(() => import("@src/components/Pages/HomePage"));
const UserPageTemplate = lazy(
  () => import("@src/components/Pages/UserPage/UserPageTemplate"),
);
const RowPage = lazy(() => import("@src/components/Pages/RowPage"));
const TimelinePage = lazy(() => import("@src/components/Pages/TimelinePage"));
const ExplorePage = lazy(() => import("@src/components/Pages/ExplorePage"));
const AdminPage = lazy(() => import("@src/components/Pages/AdminPage"));
const ActionPage = lazy(
  () => import("@src/components/Pages/ActionPage/ActionPage"),
);
const OAuthCallbackPage = lazy(
  () => import("@src/components/Pages/OAuthCallbackPage"),
);
const StyleGuidePage = lazy(
  () => import("@src/components/Pages/StyleGuidePage"),
);

export const RouterHandler = () => {
  const {
    current: modalCurrent,
    open: openModal,
    back: modalBack,
    forward: modalForward,
    canGoBack,
    canGoForward,
  } = useModalHistory();
  const dialogRef = useRef<HTMLDialogElement | null>(null);
  // Chart host ref shared across pages: always the GL renderer's wrapper
  // <div> since Phase 3.4 (the name and the ChartHostElement union predate
  // the SVG renderer's deletion).
  const svgRef = useRef<ChartHostElement>(null);
  // useCallback + the useMemo below: this trio is ModalContext's value, so
  // its identity must survive RouterHandler re-renders (resize ticks, auth
  // state) — an unstable value would invalidate every modal consumer,
  // including the rAF-driven GL chart.
  const openDialog = useCallback(() => {
    if (dialogRef.current) dialogRef.current.showModal();
  }, []);
  const closeDialog = useCallback(() => {
    if (dialogRef.current) dialogRef.current.close();
  }, []);
  const modalApi = useMemo(
    () => ({ openDialog, closeDialog, setModalContent: openModal }),
    [openDialog, closeDialog, openModal],
  );
  const defaultModalContentState = {
    modalTitle: "Default Modal Title",
    dialogWidth: "50%",
    modalContent: <p>default modal content.</p>,
    modalComponent: null,
    modalProps: {},
    dialogFooterContent: <p>default footer content.</p>,
    errorModal: false,
    hideClose: false,
  };
  const modalContent = modalCurrent ?? defaultModalContentState;
  const defaultChartWidth = Math.round(window.innerWidth * 0.95);
  const [chartWidth, setChartWidth] = useState(defaultChartWidth);
  const [rowsIncludedState, setRowsIncludedState] = useState<any[]>([]);
  const [loggedInUserID, setLoggedInUserID] = useState<string | null>(null);
  const [userIDReady, setUserIDReady] = useState(false);
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(
      firebase_auth,
      async (firebaseUser) => {
        if (firebaseUser) {
          if (firebaseUser.emailVerified === false) {
            await firebaseUser.reload();
            await firebaseUser.getIdToken(true);
          }
          setLoggedInUserID(firebaseUser.uid);
        }
        setUserIDReady(true);
      },
    );
    return () => unsubscribe();
  }, []);

  const { data: loggedInUser } = useUserDocumentQuery(
    loggedInUserID,
    loggedInUserID,
    userIDReady,
  );

  useEffect(() => {
    const handleResize = () => {
      setChartWidth(window.innerWidth * 0.95);
    };
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);
  return (
    <ModalContext.Provider value={modalApi}>
      <Header
        svgRef={svgRef}
        loggedInUser={loggedInUser}
        rowsIncludedState={rowsIncludedState}
        setRowsIncludedState={setRowsIncludedState}
        userIDReady={userIDReady}
      />
      {/* Catches lazy-route chunk load failures (e.g. a stale deploy's chunk
          names) and page render errors — without this, a failed chunk left
          the Suspense fallback up forever. */}
      <ErrorBoundary
        fallback={
          <div className="page_container">
            <p>Something went wrong loading this page.</p>
            <button
              type="button"
              className="btn btn--secondary"
              onClick={() => window.location.reload()}
            >
              Reload
            </button>
          </div>
        }
      >
        <Suspense fallback={<div className="page_container">Loading...</div>}>
          <Routes>
            <Route
              path="/"
              element={
                <HomePage
                  svgRef={svgRef}
                  loggedInUser={loggedInUser}
                  chartWidth={chartWidth}
                  rowsIncludedState={rowsIncludedState}
                  setRowsIncludedState={setRowsIncludedState}
                />
              }
            />
            <Route
              path="/user/:ownerID"
              element={<UserPageTemplate loggedInUser={loggedInUser} />}
            />
            <Route
              path="/user/:ownerID/row/:chartID"
              element={
                <RowPage
                  svgRef={svgRef}
                  loggedInUser={loggedInUser}
                  chartWidth={chartWidth}
                  rowsIncludedState={rowsIncludedState}
                  setRowsIncludedState={setRowsIncludedState}
                />
              }
            />
            <Route
              path="/user/:ownerID/timeline/:chartID"
              element={
                <TimelinePage
                  svgRef={svgRef}
                  loggedInUser={loggedInUser}
                  chartWidth={chartWidth}
                  rowsIncludedState={rowsIncludedState}
                  setRowsIncludedState={setRowsIncludedState}
                />
              }
            />

            <Route
              path="/explore"
              element={<ExplorePage loggedInUser={loggedInUser} />}
            />
            <Route
              path="/action_url"
              element={<ActionPage loggedInUser={loggedInUser} />}
            />
            <Route path="/admin" element={<AdminPage />} />
            {import.meta.env.DEV && (
              <Route path="/styleguide" element={<StyleGuidePage />} />
            )}
            <Route
              path="/oauth/callback/:providerId"
              element={
                <OAuthCallbackPage
                  loggedInUserID={loggedInUserID}
                  userIDReady={userIDReady}
                />
              }
            />
            <Route
              path="/about"
              element={
                <>
                  <div className="page_container">
                    <LockupContainer
                      title="🏗️"
                      copy="Content coming soon"
                      fontSize={5}
                    />
                  </div>
                </>
              }
            />
            <Route
              path="/blog"
              element={
                <>
                  <div className="page_container">
                    <LockupContainer
                      title="🏗️"
                      copy="Content coming soon"
                      fontSize={5}
                    />
                  </div>
                </>
              }
            />
            <Route
              path="/faq"
              element={
                <>
                  <div className="page_container">
                    <LockupContainer
                      title="🏗️"
                      copy="Content coming soon"
                      fontSize={5}
                      opacity="1"
                    />
                  </div>
                </>
              }
            />
            <Route
              path="/contact"
              element={
                <>
                  <div className="page_container">
                    <LockupContainer
                      title="🏗️"
                      copy="Content coming soon"
                      fontSize={5}
                      opacity="1"
                    />
                  </div>
                </>
              }
            />
            <Route
              path="/documentation/formatting-guide"
              element={
                <>
                  <div className="page_container">
                    <LockupContainer
                      title="🏗️"
                      copy="Content coming soon"
                      fontSize={5}
                      opacity="1"
                    />
                  </div>
                </>
              }
            />
            <Route
              path="/legal/terms_of_service"
              element={
                <>
                  <div className="page_container">
                    <LockupContainer
                      title="📄"
                      copy="Coming soon"
                      fontSize={5}
                      opacity="1"
                    />
                  </div>
                </>
              }
            />
            <Route
              path="/legal/privacy_policy"
              element={
                <>
                  <div className="page_container">
                    <LockupContainer
                      title="📄"
                      copy="Coming soon"
                      fontSize={5}
                      opacity="1"
                    />
                  </div>
                </>
              }
            />
            <Route
              path="/legal/data_policy"
              element={
                <>
                  <div className="page_container">
                    <LockupContainer
                      title="📄"
                      copy="Coming soon"
                      fontSize={5}
                      opacity="1"
                    />
                  </div>
                </>
              }
            />

            <Route
              path="*"
              element={
                <>
                  <div className="page_container">
                    <LockupContainer
                      title="404"
                      copy="We could not find a page at the current URL you have specified."
                      fontSize={5}
                      opacity="1"
                    />
                  </div>
                </>
              }
            />
          </Routes>
        </Suspense>
      </ErrorBoundary>
      <Footer />
      <Modal
        dialogRef={dialogRef}
        dialogWidth={modalContent.dialogWidth}
        modalContent={modalContent.modalContent}
        modalComponent={modalContent.modalComponent}
        modalProps={modalContent.modalProps}
        modalTitle={modalContent.modalTitle}
        dialogFooterContent={modalContent.dialogFooterContent}
        hideClose={modalContent.hideClose}
        onBack={modalBack}
        onForward={modalForward}
        canGoBack={canGoBack}
        canGoForward={canGoForward}
      />
    </ModalContext.Provider>
  );
};
